import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Inbox as InboxIcon, Loader2, ShieldCheck, PenLine } from "lucide-react";
import ApprovalRejectDialog from "@/components/ApprovalRejectDialog";
import { money } from "@/lib/format";
import { RocketFuelLink, fmtDay } from "@/components/BidOpsChips";
import SignatureSetupDialog from "@/components/SignatureSetupDialog";
import { SignatureNudge, ExecSummaryLink, ProposalReadLink } from "@/components/ApprovalBits";

function SignoffSummary({ a }) {
  const s = a.summary;
  if (!s) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600" data-testid={`approval-summary-${a.approval_id}`}>
      <span><span className="text-slate-400">{a.kind === "portfolio" ? "Sites" : "Stage"}</span> {a.kind === "portfolio" ? (s.sites?.length ?? "") : s.stage}</span>
      <span><span className="text-slate-400">Round</span> {s.round}{s.last_sent_at ? ` (last sent ${fmtDay(s.last_sent_at)} · ${money(s.last_sent_total)})` : ""}</span>
      <span className="font-mono font-semibold text-slate-900">{money(a.total ?? s.current_total)}</span>
      {a.kind === "portfolio" ? (
        <span><span className="text-slate-400">Credit</span> {money(s.credit)} <span className="text-slate-400">· sites alone</span> {money(s.standalone_total)}</span>
      ) : (<>
        <span><span className="text-slate-400">Client due</span> {fmtDay(s.due_date)}</span>
        <span><span className="text-slate-400">Internal due</span> {fmtDay(s.internal_due_date)}</span>
      </>)}
      <RocketFuelLink url={s.rocketfuel_url} label testid={`approval-rocketfuel-${a.approval_id}`} />
    </div>
  );
}

const STATUS = {
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
  approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" },
  rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Changes requested" },
  cancelled: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Cancelled" },
};

export default function Approvals() {
  const [inbox, setInbox] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSig, setHasSig] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const execFor = searchParams.get("exec");

  const load = () => {
    Promise.all([api.get("/approvals/inbox"), api.get("/approvals/mine")])
      .then(([a, b]) => { setInbox(a.data); setMine(b.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/me/signature").then((r) => setHasSig(!!r.data.signature)).catch(() => setHasSig(false)); }, []);

  const approve = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/approvals/${id}/respond`, { action: "approve" }); toast.success(r.data?.approval?.key ? `Approved · ${r.data.approval.key}` : "Approved"); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); } finally { setBusy(false); }
  };
  const doReject = async (payload) => {
    setBusy(true);
    try { await api.post(`/approvals/${rejectFor}/respond`, { action: "reject", ...payload }); toast.success("Sent back"); setRejectFor(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send back"); } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8" data-testid="approvals-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">My Approvals</h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-6">Bids waiting on your sign-off, and the status of the ones you've submitted.</p>

      <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><InboxIcon className="w-4 h-4 text-[#003E87]" /> Waiting on you <span className="text-xs font-normal text-slate-400">({inbox.length})</span></div>
      {inbox.length === 0 ? (
        <Card className="p-6 text-sm text-slate-400 mb-8" data-testid="approvals-inbox-empty">Nothing needs your approval right now.</Card>
      ) : (
        <div className="space-y-2 mb-8">
          {inbox.map((a) => (
            <Card key={a.approval_id} className="p-4" data-testid={`approval-inbox-${a.approval_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <button onClick={() => navigate(`/bids/${a.bid_id}`)} className="text-sm font-semibold text-slate-900 hover:underline truncate block text-left">{a.bid_name || a.bid_id}</button>
                  <div className="text-xs text-slate-500 mt-0.5">{a.kind === "bdm" ? "BDM sign-off · " : ""}From {a.submitted_by_name} · step {a.step_no} of {a.step_count}{a.note ? ` · “${a.note}”` : ""}</div>
                  <SignoffSummary a={a} />
                  <ProposalReadLink approval={a} />
                  <ExecSummaryLink approval={a} compact={execFor !== a.approval_id} />
                  {a.is_resubmit && a.prev_reject && (
                    <div className="text-[11px] text-rose-600 mt-1" data-testid={`inbox-resubmit-${a.approval_id}`}>Resubmitted — you asked: “{a.prev_reject.comment}”</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <Button size="sm" variant="outline" data-testid={`inbox-reject-${a.approval_id}`} disabled={busy} onClick={() => setRejectFor(a.approval_id)} className="border-rose-200 text-rose-700 hover:bg-rose-50"><XCircle className="w-3.5 h-3.5 mr-1" /> {a.kind === "bdm" ? "Request edits" : "Changes"}</Button>
                  <Button size="sm" data-testid={`inbox-approve-${a.approval_id}`} disabled={busy} onClick={() => approve(a.approval_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-slate-400" /> Your requests</div>
      {mine.length === 0 ? (
        <Card className="p-6 text-sm text-slate-400" data-testid="approvals-mine-empty">You haven't submitted anything for approval yet.</Card>
      ) : (
        <div className="space-y-2">
          {mine.map((a) => {
            const s = STATUS[a.status] || STATUS.pending;
            return (
              <Card key={a.approval_id} className="p-4" data-testid={`approval-mine-${a.approval_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <button onClick={() => navigate(`/bids/${a.bid_id}`)} className="text-sm font-semibold text-slate-900 hover:underline truncate block text-left">{a.bid_name || a.bid_id}</button>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.status === "pending" ? `Ball in court: ${a.current_approver_names.join(", ") || "—"} · step ${a.step_no} of ${a.step_count}` :
                       a.status === "rejected" ? `Changes requested by ${a.reject?.by_name}${a.open_flags ? ` · ${a.open_flags} flag${a.open_flags === 1 ? "" : "s"} to clear on the bid` : " · all flags cleared"}` :
                       a.status === "approved" ? `Fully approved${a.approval?.key ? ` · ${a.approval.key}` : ""}${a.approval?.by_name ? ` · ${a.approval.by_name}` : ""}` : "Cancelled"}
                    </div>
                    {a.status === "rejected" && a.reject?.comment && <div className="text-xs text-rose-700 mt-1">“{a.reject.comment}”</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <ApprovalRejectDialog open={!!rejectFor} onClose={() => setRejectFor(null)} onReject={doReject} busy={busy} />
      <SignatureSetupDialog open={sigOpen} onClose={() => setSigOpen(false)} onSaved={() => setHasSig(true)} />
    </div>
  );
}
